import { useEffect, useState } from 'react';
import {
  isEnrichmentInFlight,
  isLinkInFlight,
  isScreenshotInFlight,
  MAX_IN_FLIGHT_POLL_MS,
} from '@/lib/link-progress';
import type { LinkSummary } from '@/types/api';

export interface LinkProgress {
  /** Background work is in flight AND this page is still following it (see MAX_IN_FLIGHT_POLL_MS). */
  watching: boolean;
  enriching: boolean;
  capturing: boolean;
}

/**
 * What the page should show as "in progress" for `link`. A link that stays in flight past the cap
 * is stuck rather than busy, so it stops reading as busy and stops being polled; any change of
 * state (a retry, a worker picking it up) starts a fresh window.
 */
export function useLinkProgress(
  link: Pick<LinkSummary, 'processingStatus' | 'screenshotStatus' | 'updatedAt'> | undefined,
): LinkProgress {
  const inFlight = link ? isLinkInFlight(link) : false;
  const stateKey = link
    ? `${link.processingStatus}|${link.screenshotStatus}|${link.updatedAt}`
    : '';
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    setExpired(false);
    if (!inFlight) return;
    const timer = setTimeout(() => setExpired(true), MAX_IN_FLIGHT_POLL_MS);
    return () => clearTimeout(timer);
  }, [inFlight, stateKey]);

  const watching = inFlight && !expired;
  return {
    watching,
    enriching: watching && link !== undefined && isEnrichmentInFlight(link),
    capturing: watching && link !== undefined && isScreenshotInFlight(link),
  };
}
